/**
 * VROGRenderer — Virtual ROG canvas rendering library
 *
 * Usage:
 *   const vrog = new VROGRenderer(container, imgSrc, shelfInfo, backgroundColor);
 *   await vrog.render();
 *   vrog.highlightByKey('LABEL_CODE');
 *   vrog.clearHighlight();
 *   vrog.destroy();
 */
class VROGRenderer {
  /**
   * @param {HTMLElement} container         - Target DOM element to render into
   * @param {string}      imgSrc            - rogImage or shelfImage URL
   * @param {Array}       shelfInfo         - rogShelfInfo array (2D: row -> cell)
   * @param {string}      [backgroundColor='#000000']
   */
  constructor (container, imgSrc, shelfInfo, backgroundColor = '#000000') {
    this._container = container
    this._imgSrc = imgSrc
    this._shelfInfo = shelfInfo
    this._bgColor = backgroundColor
    this._cPos = [] // { key, dispX, dispY, dispW, dispH }[]
    this._overlay = null // overlay canvas for highlight
    this._cW = 0
    this._cH = 0
  }

  /** Draws the canvas from imgSrc and shelfInfo. Returns a Promise. */
  async render () {
    const container = this._container
    if (!container) throw new Error('container is required')

    container.innerHTML = '<div style="padding:18px;text-align:center;color:#6b7280;font-size:11px;">Loading image…</div>'

    const clusters = this._flattenShelfInfo(this._shelfInfo)
    if (!clusters.length) {
      container.innerHTML = '<div style="padding:16px;color:#6b7280;font-size:11px;">No data</div>'
      return
    }

    let img
    try {
      img = await this._loadImage(this._imgSrc)
    } catch {
      container.innerHTML = '<div style="padding:16px;color:#f87171;font-size:11px;">Failed to load image</div>'
      return
    }

    container.innerHTML = ''

    // Compute bounding box that covers all clusters
    let minX = Infinity; let maxX = -Infinity; let minY = Infinity; let maxY = -Infinity
    for (const c of clusters) {
      const [x1, y1, x2, y2] = c.cluster_bbox
      if (x1 < minX) minX = x1
      if (x2 > maxX) maxX = x2
      if (y1 < minY) minY = y1
      if (y2 > maxY) maxY = y2
    }
    const tW = maxX - minX; const tH = maxY - minY
    if (tW <= 0 || tH <= 0) return

    // Canvas dimensions: fixed height of 473px, width scaled proportionally
    const FIXED_H = 473
    const sc = FIXED_H / tH
    const cW = Math.round(tW * sc)
    const cH = FIXED_H
    const dpr = window.devicePixelRatio || 1

    this._cW = cW
    this._cH = cH

    // Main canvas (HiDPI-aware)
    const canvas = document.createElement('canvas')
    canvas.width = Math.round(cW * dpr)
    canvas.height = Math.round(cH * dpr)
    canvas.style.cssText = `width:${cW}px;height:${cH}px;border-radius:4px;display:block;`

    const ctx = canvas.getContext('2d')
    ctx.scale(dpr, dpr)
    ctx.fillStyle = this._bgColor
    ctx.fillRect(0, 0, cW, cH)

    // Draw each cluster: crop from source image, paste into scaled position, then label
    this._cPos = []
    for (const cluster of clusters) {
      const [x1, y1, x2, y2] = cluster.cluster_bbox
      const bW = x2 - x1; const bH = y2 - y1
      if (bW <= 0 || bH <= 0) continue

      const dX = Math.round((x1 - minX) * sc)
      const dY = Math.round((y1 - minY) * sc)
      const dW = Math.max(1, Math.round((x2 - minX) * sc) - dX)
      const dH = Math.max(1, Math.round((y2 - minY) * sc) - dY)

      // eslint-disable-next-line camelcase
      const key = cluster.esl_info?.[0]?.label_code || ''
      this._cPos.push({ key, dispX: dX, dispY: dY, dispW: dW, dispH: dH })

      try {
        ctx.drawImage(img, x1, y1, bW, bH, dX, dY, dW, dH)
      } catch {
        ctx.fillStyle = '#e2e5ef'
        ctx.fillRect(dX, dY, dW, dH)
      }

      // Cluster border
      ctx.save()
      ctx.strokeStyle = 'rgba(255,255,255,0.22)'
      ctx.lineWidth = 1
      ctx.strokeRect(dX + 0.5, dY + 0.5, dW - 1, dH - 1)
      ctx.restore()

      // Product name label
      // eslint-disable-next-line camelcase
      const articleName = cluster.prd_info?.[0]?.article_name || ''
      if (articleName && dW >= 20 && dH >= 16) {
        this._drawLabel(ctx, articleName, dX, dY, dW, dH)
      }
    }

    // Transparent overlay canvas for highlight effect
    const overlay = document.createElement('canvas')
    overlay.width = cW
    overlay.height = cH
    overlay.style.cssText = `position:absolute;inset:0;width:${cW}px;height:${cH}px;pointer-events:none;border-radius:4px;`
    this._overlay = overlay

    const wrapper = document.createElement('div')
    wrapper.style.cssText = 'position:relative;display:inline-block;line-height:0;'
    wrapper.appendChild(canvas)
    wrapper.appendChild(overlay)
    container.appendChild(wrapper)
  }

  /**
   * Dims all clusters except the one matching the given label_code.
   * @param {string} key - esl_info[0].label_code
   */
  highlightByKey (key) {
    if (!this._overlay) return
    const octx = this._overlay.getContext('2d')
    const { _cW: cW, _cH: cH } = this
    octx.clearRect(0, 0, cW, cH)
    if (!key) return
    const target = this._cPos.find(p => p.key === key)
    if (!target) return
    // Dim everything, then punch out the target cluster
    octx.fillStyle = 'rgba(0,0,0,0.55)'
    octx.fillRect(0, 0, cW, cH)
    octx.clearRect(target.dispX, target.dispY, target.dispW, target.dispH)
  }

  /** Removes the highlight overlay. */
  clearHighlight () {
    if (!this._overlay) return
    this._overlay.getContext('2d').clearRect(0, 0, this._cW, this._cH)
  }

  /** Changes the background color and re-renders. */
  async setBackgroundColor (color) {
    this._bgColor = color
    await this.render()
  }

  /** Clears the container and releases references. */
  destroy () {
    if (this._container) this._container.innerHTML = ''
    this._overlay = null
    this._cPos = []
  }

  // ── Private helpers ──

  _flattenShelfInfo (shelfInfo) {
    const clusters = []
    for (const row of (shelfInfo || [])) {
      for (const cell of (row || [])) {
        if (cell) clusters.push(cell)
      }
    }
    return clusters
  }

  _loadImage (src) {
    return new Promise((resolve, reject) => {
      const el = new Image()
      el.crossOrigin = 'anonymous'
      el.onload = () => resolve(el)
      el.onerror = () => reject(new Error('Image load failed'))
      el.src = src
    })
  }

  _drawLabel (ctx, text, dX, dY, dW, dH) {
    ctx.save()
    const fs = Math.min(10, Math.max(7, Math.floor(dW / 12))) // font size clamped 7–10px
    const lH = fs + 2 // line height
    const mL = Math.min(2, Math.floor((dH - 4) / lH)) // max lines that fit
    if (mL < 1) { ctx.restore(); return }

    ctx.font = `${fs}px sans-serif`
    ctx.textAlign = 'center'
    ctx.textBaseline = 'bottom'
    ctx.shadowColor = 'rgba(0,0,0,0.95)'
    ctx.shadowBlur = 4
    ctx.fillStyle = 'rgba(255,255,255,0.88)'

    // Word-wrap
    const maxTW = dW - 6
    const words = text.split(/\s+/)
    let lines = []; let cur = ''
    for (const w of words) {
      const t = cur ? `${cur} ${w}` : w
      if (ctx.measureText(t).width > maxTW && cur) {
        lines.push(cur); cur = w
      } else {
        cur = t
      }
    }
    if (cur) lines.push(cur)
    if (lines.length > mL) lines = lines.slice(0, mL)

    // Truncate lines that are still too wide
    lines = lines.map(line => {
      if (ctx.measureText(line).width <= maxTW) return line
      let t = line
      while (t.length > 0 && ctx.measureText(t + '…').width > maxTW) t = t.slice(0, -1)
      return t + '…'
    })

    // Draw bottom-up
    let bY = dY + dH - 3
    for (let i = lines.length - 1; i >= 0; i--) {
      ctx.fillText(lines[i], dX + dW / 2, bY)
      bY -= lH
    }
    ctx.restore()
  }
}

// CommonJS / ESM compatibility
if (typeof module !== 'undefined' && module.exports) module.exports = VROGRenderer
